import { Component, EventEmitter, Input, Output } from '@angular/core';

import {
  ActionMenuComponent,
  HoverIdComponent,
  PagedFeedComponent,
} from '@clinic-platform/ui-components';
import { ClinicOperationalSettingsDto } from '../data-access/settings.models';

@Component({
  selector: 'app-clinic-settings-list',
  standalone: true,
  imports: [
    ActionMenuComponent,
    HoverIdComponent,
    PagedFeedComponent,
  ],
  template: `
    <lib-paged-feed
      [items]="items"
      [collectionTemplate]="clinicsTableTemplate"
      [loading]="loading"
      [hasMore]="false"
      [errorText]="errorText"
      [emptyText]="'No ' + clinicLabelPluralLower + ' found for this company.'"
      retryText="Retry"
      (retry)="retry.emit()"
    />

    <ng-template #clinicsTableTemplate let-pageItems="items">
      <section class="clinic-settings-list__surface">
        <div class="clinic-settings-list__surface-head">
          <div>
            <h3>{{ clinicLabelPlural }}</h3>
            <p>{{ pageItems.length }} shown / {{ totalCount }} total</p>
          </div>
        </div>

        <div class="clinic-settings-list__table-wrap">
          <table class="clinic-settings-list__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>City</th>
                <th>Address</th>
                <th>Working Day</th>
                <th>Slot</th>
                <th>Timezone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (clinic of pageItems; track clinic.clinicId) {
                <tr class="clinic-settings-list__row" (click)="view.emit(clinic)">
                  <td>
                    <lib-hover-id [value]="clinic.clinicId">
                      <span class="clinic-settings-list__name">{{ clinic.clinicName }}</span>
                    </lib-hover-id>
                  </td>
                  <td>{{ clinic.city || '-' }}</td>
                  <td>{{ clinic.address || '-' }}</td>
                  <td>{{ clinic.workingDayStart }} -> {{ clinic.workingDayEnd }}</td>
                  <td>
                    <div>{{ clinic.effectiveSlotDurationMinutes ?? 0 }} min</div>
                    <div class="clinic-settings-list__muted">{{ slotOverrideLabel(clinic) }}</div>
                  </td>
                  <td>{{ timezoneLabel(clinic) }}</td>
                  <td class="clinic-settings-list__actions-cell" (click)="$event.stopPropagation()">
                    <lib-action-menu [menuTemplate]="clinicActionsMenu" buttonAriaLabel="Open clinic actions">
                      <ng-template #clinicActionsMenu let-close="close">
                        <button type="button" class="action-menu__item" (click)="view.emit(clinic); close()">View</button>
                        <button type="button" class="action-menu__item action-menu__item--primary" (click)="settings.emit(clinic); close()">Settings</button>
                        <button type="button" class="action-menu__item" (click)="edit.emit(clinic); close()">Edit</button>
                        <button type="button" class="action-menu__item action-menu__item--danger" (click)="delete.emit(clinic); close()">Delete</button>
                      </ng-template>
                    </lib-action-menu>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <div class="clinic-settings-list__cards">
          @for (clinic of pageItems; track clinic.clinicId) {
            <article class="clinic-settings-list__card-item" (click)="view.emit(clinic)">
              <div class="clinic-settings-list__card-head">
                <div>
                  <lib-hover-id [value]="clinic.clinicId">
                    <div class="clinic-settings-list__card-title">{{ clinic.clinicName }}</div>
                  </lib-hover-id>
                  <div class="clinic-settings-list__card-subtitle">{{ clinic.city || '-' }}</div>
                </div>
                <div class="clinic-settings-list__card-actions" (click)="$event.stopPropagation()">
                  <lib-action-menu [menuTemplate]="clinicCardActionsMenu" buttonAriaLabel="Open clinic actions">
                    <ng-template #clinicCardActionsMenu let-close="close">
                      <button type="button" class="action-menu__item" (click)="view.emit(clinic); close()">View</button>
                      <button type="button" class="action-menu__item action-menu__item--primary" (click)="settings.emit(clinic); close()">Settings</button>
                      <button type="button" class="action-menu__item" (click)="edit.emit(clinic); close()">Edit</button>
                      <button type="button" class="action-menu__item action-menu__item--danger" (click)="delete.emit(clinic); close()">Delete</button>
                    </ng-template>
                  </lib-action-menu>
                </div>
              </div>

              <div class="clinic-settings-list__card-grid">
                <div>
                  <span>Working Day</span>
                  <strong>{{ clinic.workingDayStart }} -> {{ clinic.workingDayEnd }}</strong>
                </div>
                <div>
                  <span>Slot</span>
                  <strong>{{ clinic.effectiveSlotDurationMinutes ?? 0 }} min</strong>
                </div>
                <div>
                  <span>Timezone</span>
                  <strong>{{ timezoneLabel(clinic) }}</strong>
                </div>
                <div>
                  <span>Address</span>
                  <strong>{{ clinic.address || '-' }}</strong>
                </div>
              </div>
            </article>
          }
        </div>
      </section>
    </ng-template>
  `,
  styles: [`
    .clinic-settings-list__surface {
      display: grid;
      gap: 14px;
      padding: 16px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: rgba(255, 255, 255, 0.94);
    }

    .clinic-settings-list__surface-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
    }

    .clinic-settings-list__surface-head h3 {
      margin: 0;
      color: #0f172a;
      font-size: 18px;
    }

    .clinic-settings-list__surface-head p {
      margin: 4px 0 0;
      color: #475569;
      font-size: 13px;
    }

    .clinic-settings-list__table-wrap {
      overflow-x: auto;
    }

    .clinic-settings-list__table {
      width: 100%;
      min-width: 940px;
      border-collapse: collapse;
    }

    .clinic-settings-list__table th,
    .clinic-settings-list__table td {
      border-bottom: 1px solid #eef2f7;
      padding: 12px 10px;
      text-align: left;
      vertical-align: middle;
    }

    .clinic-settings-list__table th {
      background: rgba(248, 250, 252, 0.9);
      color: #0f172a;
      font-weight: 700;
    }

    .clinic-settings-list__row {
      cursor: pointer;
    }

    .clinic-settings-list__row:hover {
      background: rgba(239, 246, 255, 0.6);
    }

    .clinic-settings-list__name {
      color: #0f172a;
      font-weight: 700;
    }

    .clinic-settings-list__muted {
      margin-top: 3px;
      color: #64748b;
      font-size: 12px;
    }

    .clinic-settings-list__actions-cell {
      width: 1%;
      white-space: nowrap;
    }

    .clinic-settings-list__cards {
      display: none;
      gap: 12px;
    }

    .clinic-settings-list__card-item {
      display: grid;
      gap: 12px;
      padding: 14px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
    }

    .clinic-settings-list__card-item:hover {
      background: rgba(239, 246, 255, 0.6);
    }

    .clinic-settings-list__card-head {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
    }

    .clinic-settings-list__card-title {
      color: #0f172a;
      font-size: 16px;
      font-weight: 700;
    }

    .clinic-settings-list__card-subtitle {
      margin-top: 4px;
      color: #64748b;
      font-size: 13px;
    }

    .clinic-settings-list__card-grid {
      display: grid;
      gap: 8px;
    }

    .clinic-settings-list__card-grid span {
      color: #64748b;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
    }

    .clinic-settings-list__card-grid strong {
      display: block;
      margin-top: 3px;
      color: #0f172a;
      font-size: 14px;
    }

    @media (max-width: 900px) {
      .clinic-settings-list__table-wrap {
        display: none;
      }

      .clinic-settings-list__cards {
        display: grid;
      }
    }

    @media (max-width: 640px) {
      .clinic-settings-list__surface {
        padding: 12px;
      }
    }
  `],
})
export class ClinicSettingsListComponent
{
  @Input() public items: ClinicOperationalSettingsDto[] = [];
  @Input() public totalCount = 0;
  @Input() public loading = false;
  @Input() public errorText: string | null = null;
  @Input() public clinicLabelPlural = 'Clinics';
  @Input() public clinicLabelPluralLower = 'clinics';

  @Output() public readonly retry = new EventEmitter<void>();
  @Output() public readonly view = new EventEmitter<ClinicOperationalSettingsDto>();
  @Output() public readonly settings = new EventEmitter<ClinicOperationalSettingsDto>();
  @Output() public readonly edit = new EventEmitter<ClinicOperationalSettingsDto>();
  @Output() public readonly delete = new EventEmitter<ClinicOperationalSettingsDto>();

  protected slotOverrideLabel(clinic: ClinicOperationalSettingsDto): string {
    const override = clinic.slotDurationMinutesOverride ?? 0;
    return override > 0 ? `Override: ${override} min` : 'Inherited slot';
  }

  protected timezoneLabel(clinic: ClinicOperationalSettingsDto): string {
    return clinic.timeZoneSource === 'clinic_override'
      ? clinic.timeZone
      : (clinic.timeZoneInherited ?? clinic.timeZone);
  }
}
